import { Offering } from '../model/offering.types';
import {
  LastMinutePackage,
  Package,
} from '~entities/package/model/package.types';
import { Room } from '~entities/room/model/room.types';

// Offering classification, by the `type` tag stamped once at the single
// construction site -- not by shape.
//
//   OneConfigurationDTO
//     roomCategories[]      -,
//     packageCategories[]   -+-> transformConfigurationDtoToConfiguration()
//     lastMinutePackages[]  -'      stamps type: 'ROOM' | 'PACKAGE' | 'LAST_MINUTE_PACKAGE'
//                                    |
//                                    v
//                  Offering = Room | Package | LastMinutePackage
//                                    |
//                        switch (offering.type)   -- exhaustive, `never` default
//                           'ROOM'                -> RoomCard,               no badge
//                           'PACKAGE'             -> PackageCard,            "Pauschale"
//                           'LAST_MINUTE_PACKAGE' -> LastMinutePackageCard,  LMP label
//
// Before this tag existed, these guards sniffed the offering's shape with a
// Zod safeParse per kind. That doubled as an incidental sanity check on
// mapper output -- a room whose optional `size` arrived as a number made
// isRoom() return false, so a malformed offering silently rendered no card at
// all. The tag check does not repeat that check: a malformed-but-tagged
// offering now renders the right card with one odd field instead of
// vanishing (samera/samera#3515).

/**
 * Type guard for determining if an offering is a room.
 *
 * @param offering The offering to check.
 *
 * @returns A type predicate indicating if the offering is a room.
 */
export const isRoom = (offering?: Offering): offering is Room =>
  offering?.type === 'ROOM';

/**
 * Type guard for determining if an offering is a package.
 *
 * @param offering The offering to check.
 *
 * @returns A type predicate indicating if the offering is a package.
 */
export const isPackage = (offering?: Offering): offering is Package =>
  offering?.type === 'PACKAGE';

/**
 * Type guard for determining if an offering is a last minute package.
 *
 * @param offering The offering to check.
 *
 * @returns A type predicate indicating if the offering is a last minute package.
 */
export const isLastMinutePackage = (
  offering?: Offering,
): offering is LastMinutePackage => offering?.type === 'LAST_MINUTE_PACKAGE';
